import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, LinearProgress } from "@material-ui/core";
import { Subtitle } from "ui/componentes/Layout";
import { Publish, Search } from "@material-ui/icons";
import { SuccessButton } from "ui/componentes/SuccessButton";
import { TaskState } from "config/firebaseConfig";
import { UploadState, useUploadFiles } from "api/hooks/useUploadFiles";
import { StorageFile } from "api/model/StorageFile";

export interface FileUploadProps {
    name: string;
    chooseBtnLabel?: string;
    uploadBtnLabel?: string;
    cancelBtnLabel?: string;
    accept?: string;
    onComplete?: (uploadState: UploadState, storageFile?: StorageFile) => void;
    folder: string;
    disabled?: boolean;
    autoUpload?: boolean;
}

const ImageThumb = ({ image }) => {
    if (!image) return null;
    return (
        <Box>
            <img
                src={URL.createObjectURL(image)}
                alt={image.name}
                style={{
                    maxWidth: "100%",
                    maxHeight: "80px",
                }}
            />
        </Box>
    );
};

const FileUpload: React.FC<FileUploadProps> = ({
    name,
    uploadBtnLabel = "Upload",
    chooseBtnLabel = "File",
    cancelBtnLabel = "Cancel",
    accept,
    folder,
    onComplete,
    disabled = false,
    autoUpload = true,
}) => {
    const [fileToUpload, setFileToUpload] = useState<File | undefined>();
    let { uploadFile, uploadState } = useUploadFiles({
        onComplete,
        formFieldName: name,
    });
    const { state } = uploadState || {};

    const loading = useMemo(() => {
        return state === TaskState.RUNNING;
    }, [state]);

    useEffect(() => {
        if (state === TaskState.SUCCESS) {
            setFileToUpload(undefined);
        }
    }, [state]);

    return (
        <Box display="flex" alignItems="center" width="100%">
            <input
                accept={accept}
                id={name}
                type="file"
                style={{ display: "none" }}
                disabled={disabled}
                onChange={(event) => {
                    if (autoUpload && event.target.files?.[0]) {
                        uploadFile(folder, event.target.files?.[0]);
                        return;
                    }
                    setFileToUpload(event.target.files?.[0]);
                }}
            />
            <Grid container spacing={1}>
                <Grid xs={12} item hidden={!fileToUpload}>
                    <Box display="flex" width="100%" justifyContent="center">
                        <Subtitle
                            text={fileToUpload?.name}
                            color="textSecondary"
                        />
                    </Box>
                </Grid>
                <Grid xs={12} item hidden={!fileToUpload}>
                    <Box display="flex" width="100%" justifyContent="center">
                        <ImageThumb image={fileToUpload} />
                    </Box>
                </Grid>
                <Grid xs={12} item hidden={state !== TaskState.RUNNING}>
                    <div style={{ width: "100%" }}>
                        <LinearProgress
                            variant="determinate"
                            value={uploadState.progressPercentage || 0}
                        />
                    </div>
                </Grid>
                <Grid xs={12} item>
                    <Box display="flex" width="100%" justifyContent="center">
                        <label htmlFor={name}>
                            <Button
                                size="small"
                                color="primary"
                                component="span"
                                fullWidth={false}
                                startIcon={<Search />}
                                disabled={loading || disabled}
                            >
                                {chooseBtnLabel}
                            </Button>
                        </label>
                        {!autoUpload && (
                            <Button
                                size="small"
                                component="span"
                                fullWidth={false}
                                style={{ marginLeft: 5 }}
                                disabled={!fileToUpload || loading || disabled}
                                onClick={() => setFileToUpload(undefined)}
                            >
                                {cancelBtnLabel}
                            </Button>
                        )}
                        {!autoUpload && (
                            <SuccessButton
                                size="small"
                                fullWidth={false}
                                style={{ marginLeft: 5 }}
                                startIcon={<Publish />}
                                disabled={!fileToUpload || loading || disabled}
                                onClick={() => {
                                    if (!fileToUpload) return;
                                    uploadFile(folder, fileToUpload);
                                }}
                            >
                                {uploadBtnLabel}
                            </SuccessButton>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export { FileUpload };
